import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import axios, {
  AxiosInstance,
  AxiosResponse,
  CancelTokenSource, // DEPRECATED in axios 1.x - kept for backwards compatibility
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import {v4 as uuid} from 'uuid';

// Per-call opt-out for the global 401 logout interceptor. Some middleware
// endpoints return 401 with application-level semantics rather than
// session-invalid (e.g. /verification/phone returns 401 verification_failed
// for a wrong/expired OTP). Callers that handle 401 themselves can set this
// flag to keep the global interceptor from firing toast + logoutUser.
declare module 'axios' {
  interface AxiosRequestConfig {
    skipUnauthorizedHandler?: boolean;
  }
}

// TODO: DIS-XXX - Migrate from CancelToken to AbortController
// CancelToken is deprecated in axios 1.x but still works.
// AbortController is the modern replacement. Migration requires updating:
// - searchSuburbs, addressSearch, searchProductsKeyword methods
// - shopSelectionStore.ts
// - AddressPicker.tsx
// - SearchScreen.tsx, SearchScreenLoyaltyEnabled.tsx
// See: https://axios-http.com/docs/cancellation
import {config} from '../config';
import {
  AirtableAttachmentDetail,
  AirtableFeedItem,
  AirtablePromotionItem,
  HomepageResponse,
} from '../models/airtable';
import {
  BillingAddress,
  CreateZenGiftCardPaymentInstrumentRequest,
  TransferTokens,
  User,
} from '../models/mfl';
import {
  Address,
  CreateCompetitionEntryResponse,
  Department,
  EnquireVoucherErrorResponse,
  EnquireVoucherResponse,
  FeedQueryParams,
  GeocodedSuburb,
  GiftCardRecipientData,
  PaginatedFeedResponse,
  PostCompetitionEntryFormDataRequest,
  Shop,
  StoreByDistance,
} from '../models/middleware';
import {AccountScreenAction} from '../models/navigationTypes';
import {logError, logWarning} from '../utils/errorLogging';
import {dataURItoBlob, getDataUriMediaType} from '../utils/fileUpload';
import {logger, serializeError} from '../utils/logger';
import {otelPropagationInterceptor} from '../utils/telemetry/interceptor';
import {getCachedUserAgent} from '../utils/userAgent';

import {transformUserV2} from './transformers/user.transformer';
import type {CheckoutOrderData, TenderOption} from './types/checkout';
import {UserInfoV2Response, UserV2} from './types/user.type';
import {
  contextHeadersRequestInterceptor,
  jsonApiDeserialisationResponseInterceptor,
} from './apiServiceInterceptors';
import {OrderLine} from './apiServiceResponse';
import {getApiBaseUrl} from './apiUrlHelper';
import {getInterceptorCallbacks} from './interceptorCallbacks';
import type {ApplyOfferSuccessResponse} from './types';
import {
  ClientConfigResponse,
  CommunicationsPreferencesParams,
  CompetitionOptOutParams,
  CompetitionOptOutResponse,
  MobileVerificationParams,
  OfferCoffeeCardStats,
  OffersItemWithMflId,
  RewardsCheckAccountExistsParams,
  RewardsGetAccountParams,
  RewardsGetAccountResponse,
  RewardsGetLmgStaffOptionsResponse,
  RewardsInformationResponse,
  RewardsLmgStaffInviteParams,
  RewardsLmgStaffInviteResponse,
  RewardsOptInParams,
  RewardsOptOutParams,
  RewardsSignUpCompleteFailureReason,
  RewardsSignUpCompleteParams,
  RewardsSignUpCompleteResult,
  RewardsSignUpParams,
  RewardsSignUpResponse,
  RewardsSignUpResult,
  RewardsVerifyExistingPhoneNumberParams,
  RewardsVerifyNewPhoneNumberParams,
  SignUpOfferData,
  VerifyExistingUserEmailAddressParams,
  VerifyPhoneStaffFailureReason,
  VerifyPhoneStaffParams,
  VerifyPhoneStaffResult,
  ZenOffer,
} from './types';

// ============================================
// V2 Offers Endpoint Types (internal to transport layer)
// ============================================

interface V2OffersResponse {
  offers: V2EnrichedOffer[];
  meta: {
    totalCount: number;
    activatedCount: number;
    fetchedAt: string;
  };
}

interface V2EnrichedOffer {
  mflId: string;
  contentId: string;
  headline: string;
  tileHeadline: string;
  description: string;
  heroImage: V2ImageAsset | null;
  heroImageDesktop: V2ImageAsset | null;
  canApply: boolean;
  disabledReason: string | null;
  isActivated: boolean;
  coffeeCard: V2CoffeeCardProgress | null;
  hasAvailableProducts: boolean;
  linkedProductCount: number;
  barcodes?: string[];
}

interface V2ImageAsset {
  url: string;
  width: number;
  height: number;
  thumbnailUrl?: string;
}

interface V2CoffeeCardProgress {
  currentPurchases: number;
  targetPurchases: number;
  progressPercent: number;
  isComplete: boolean;
  isFullyRedeemed: boolean;
}

// ============================================
// V2 → Legacy Type Transformers
// ============================================

function transformV2ImageToAirtable(img: V2ImageAsset | null): AirtableAttachmentDetail[] {
  if (!img) return [];
  return [
    {
      url: img.url,
      width: img.width,
      height: img.height,
      type: 'image',
      filename: '',
      size: 0,
      id: '',
      thumbnails: {
        small: {url: img.thumbnailUrl || img.url, width: img.width, height: img.height},
      },
    },
  ];
}

function transformV2CoffeeCard(v2Card: V2CoffeeCardProgress): OfferCoffeeCardStats {
  return {
    offer_id: 0, // Not used after initial load
    offer_description: '',
    purchased_quantity: v2Card.currentPurchases,
    target_quantity: v2Card.targetPurchases,
    required_quantity: v2Card.targetPurchases - v2Card.currentPurchases,
    total_percentage_completed: v2Card.progressPercent,
    fully_redeemed: v2Card.isFullyRedeemed,
    card_count: 1,
    card_limit: 1,
    site_count: 1,
    site_limit: null,
    items: [],
  };
}

function transformV2OfferToLegacy(offer: V2EnrichedOffer): OffersItemWithMflId {
  const base = {
    mflId: offer.mflId,
    offerId: offer.contentId,
    headline: offer.headline,
    tileHeadline: offer.tileHeadline,
    description: offer.description,
    heroImage: transformV2ImageToAirtable(offer.heroImage),
    heroImageDesktop: transformV2ImageToAirtable(offer.heroImageDesktop),
    tenant: [] as ('SNS' | 'HB' | 'BMT')[], // Server handles tenant filtering
    products: [] as string[],
    barcodes: offer.barcodes || [],
    status: 'Approved - Live' as const,
    isActivated: offer.isActivated, // Pass through activation status from API
    canApply: offer.canApply, // Whether deal criteria match current cart
    disabledReason: offer.disabledReason, // Why deal can't be applied
  };

  if (offer.coffeeCard) {
    return {
      ...base,
      offerTrigger: 'Coffee Card' as const,
      coffeeCardTriggerPurchases: String(offer.coffeeCard.targetPurchases),
      coffeeCardStats: transformV2CoffeeCard(offer.coffeeCard),
    };
  }

  return {...base, offerTrigger: undefined};
}

logger.info('APIService', 'API Service initialized', {
  tenant: config.BASE_TENANT,
  baseURL: config.BASE_URL,
});

export const convertAppAddressToMFLAddress = (
  billingAddress: BillingAddress | null | undefined,
) => {
  return {
    lines: billingAddress?.lines?.map((line: string) => line.toUpperCase()),
    postal_code: billingAddress?.postal_code?.toUpperCase(),
    locality: billingAddress?.locality?.toUpperCase(),
    administrative_area: billingAddress?.administrative_area?.toUpperCase(),
  };
};

const convertAppUsertoMFLUser = (values: User) => {
  // dateOfBirth is already in YYYY-MM-DD format, no conversion needed
  const birthDate = values.dateOfBirth || null;

  const billingAddress = convertAppAddressToMFLAddress(values.billingAddress);

  return {
    first_name: values.firstName,
    last_name: values.lastName,
    email_address: values.email,
    birth_date: birthDate,
    phone_number: values.phoneNumber,
    billing_address: billingAddress,
    password: values.password ? values.password : null,
    bypass_compromised_password_validation: values.passwordValidationBypass || false,
  };
};

export type CompetitionEntry = Omit<
  PostCompetitionEntryFormDataRequest,
  'isOptInLmgMarketing' | 'isOptInSupplierMarketing' | 'isTermsAccepted' | 'isReceiptRequired'
> & {
  isOptInLmgMarketing: boolean;
  isOptInSupplierMarketing: boolean;
  isTermsAccepted: boolean;
};

// Interface for MFL error response structure
interface MflErrorResponse {
  errors: {
    errors: Array<{detail: string}>;
  };
}

// DIS-1190/DIS-1282: response shape returned in axios error envelopes.
//
// Session-auth responses (DIS-1324 middleware contract) carry TOP-LEVEL
// code/recovery fields that drive the 401 logout decision:
//   { error: string, code: 'session_invalid'|'refresh_failed'|..., recovery: 'reauth'|... }
//
// Staff verify-lite and rewards endpoints use a NESTED error object:
//   { error: { code?, reason?, message? } }
//
// Both shapes coexist — error is a union; guard with typeof before reading
// nested fields.
export type ErrorBody = {
  error?: string | {code?: string; reason?: string; message?: string};
  // Session-auth top-level fields (DIS-1324)
  code?: string;
  recovery?: 'reauth' | 'use_mw_session_cookie';
};

// Resolve the error response across axios adapters. The fetch adapter
// (used under MSW in CI) stores the response on err.cause.response and
// may serialize response.data as a string; the http/xhr adapter places
// it on err.response and parses JSON automatically.
export function resolveErrorBody(err: unknown): {
  status: number | undefined;
  body: ErrorBody | undefined;
} {
  if (!isAxiosError(err)) {
    return {status: undefined, body: undefined};
  }
  type CauseWithResponse = {response?: {status?: number; data?: unknown}};
  const causeResponse = (err.cause as CauseWithResponse | undefined)?.response;
  const response = err.response ?? causeResponse;
  const status = response?.status ?? err.status;
  const rawData = response?.data;
  let body: ErrorBody | undefined;
  if (typeof rawData === 'string') {
    try {
      body = JSON.parse(rawData);
    } catch {
      body = undefined;
    }
  } else if (rawData && typeof rawData === 'object') {
    body = rawData as ErrorBody;
  }
  return {status, body};
}

// DIS-1586: the one communications-preferences write failure that stays
// swallowed. The middleware returns 422 `contact_not_found` when the
// authenticated email resolves to no Zoho contact; there is nothing to write
// and nothing the member can retry, so the screen keeps its existing silent
// behaviour. Deliberately narrow — any other status, or a 422 with a different
// code, must reach the caller.
function isCommunicationsContactNotFound(err: unknown): boolean {
  const {status, body} = resolveErrorBody(err);
  if (status !== 422) return false;
  const nestedError = typeof body?.error === 'object' ? body.error : undefined;
  return nestedError?.code === 'contact_not_found';
}

function mapVerifyPhoneStaffError(err: unknown): VerifyPhoneStaffFailureReason {
  if (!isAxiosError(err)) return 'unknown';
  const {status, body} = resolveErrorBody(err);
  if (!status) return 'network';
  if (status === 400) {
    const nestedError = typeof body?.error === 'object' ? body.error : undefined;
    return nestedError?.code === 'phone_invalid' ? 'phone_invalid' : 'unknown';
  }
  // DIS-1339: the endpoint now returns 422 for verification_failed (was 401);
  // both carry the same nested error.reason, so map either status the same way.
  if (status === 401 || status === 422) {
    const nestedError = typeof body?.error === 'object' ? body.error : undefined;
    switch (nestedError?.reason) {
      case 'code_invalid':
        return 'code_invalid';
      case 'code_expired':
        return 'code_expired';
      case 'max_attempts_reached':
        return 'max_attempts';
      default:
        // DIS-1135 bug_007: unknown reason / empty body → 'unknown'
        // (was 'code_invalid'). Avoids telling the user their correct
        // code is wrong and burning Twilio attempts on retries.
        return 'unknown';
    }
  }
  if (status === 502) return 'upstream';
  if (status === 503) return 'service';
  return 'unknown';
}

function mapRewardsSignUpCompleteError(err: unknown): RewardsSignUpCompleteFailureReason {
  if (!isAxiosError(err)) return 'unknown';
  const {status, body} = resolveErrorBody(err);
  if (!status) return 'network';
  if (status === 400) {
    const nestedError = typeof body?.error === 'object' ? body.error : undefined;
    if (nestedError?.code === 'phone_invalid') return 'phone_invalid';
    if (nestedError?.message?.startsWith('Invalid staffStoreId')) return 'invalid_store_id';
    if (
      nestedError?.message?.startsWith('Invalid lmgState') ||
      nestedError?.message?.startsWith('Invalid lmgDepartment')
    ) {
      return 'invalid_lmg_selection';
    }
    return 'unknown';
  }
  // DIS-1384 (IN-1): 403 = missing/invalid lmgStaffCode on an LMG-staff complete.
  // Submit-time is the authoritative invite gate (C2); maps to the canonical
  // "invite link invalid" copy at the call site. Does not log out.
  if (status === 403) return 'invalid_lmg_code';
  if (status === 410) return 'session_expired';
  // DIS-1328: non-authoritative sign-in with no Zoho contact → clean 422. The
  // loyalty card may be valid but identity can't be verified; surface support
  // copy, never a retry. 422 also sidesteps the global 401 logout interceptor.
  if (status === 422 && body?.error?.code === 'account_not_found') return 'account_not_found';
  if (status === 500) return 'workflow_failed';
  if (status === 503) return 'service';
  return 'unknown';
}

// DIS-1328: map /rewards/verification-lite errors. Only `account_not_found` (422)
// is terminal-with-support-copy; everything else (wrong code, network, 5xx) is a
// generic `unknown` the caller renders as the inline "wrong code" message.
function mapRewardsSignUpError(err: unknown): RewardsSignUpResult & {ok: false} {
  const {status, body} = resolveErrorBody(err);
  if (status === 422 && body?.error?.code === 'account_not_found') {
    return {ok: false, reason: 'account_not_found'};
  }
  return {ok: false, reason: 'unknown'};
}

// TODO: No need for this to be a class, we can just have a functions module
// We are not using OOP in general in this codebase, the TS is mostly for type safety
class ApiService {
  private mwApi!: AxiosInstance;

  constructor() {
    this.initialise(config?.ENV);
  }

  public getApi(): AxiosInstance {
    return this.mwApi;
  }

  /**
   * Default to prd mfl sandbox. if env: 'DEV' use the mfl dev sandbox. i.e. app development shops
   * MFL_APP_ID_HB vs MFL_APP_ID_HB_DEV
   * @param env
   */
  initialise(env?: 'DEV') {
    // Fresh axios instance ⇒ no prior session context. Clear the logout latch
    // so a rebuild can't inherit a stale isLoggingOut=true.
    this.isLoggingOut = false;
    this.mwApi = axios.create({
      baseURL: getApiBaseUrl(),
      withCredentials: true, // sends mw_session cookie automatically on web
      headers: {
        Accept: 'application/json, text/plain, */*',
        tenant: config.BASE_TENANT,
        ...(env ? {env} : {}),
      },
    });

    // Response interceptors — ORDER IS CRITICAL:
    // 1st: capture x-session-id before deserializer strips AxiosResponse wrapper
    this.mwApi.interceptors.response.use(this.sessionIdExtractionInterceptor);
    // 2nd: JSON:API deserialization (may replace AxiosResponse with plain data)
    this.mwApi.interceptors.response.use(jsonApiDeserialisationResponseInterceptor(this.mwApi));
    // 3rd: body-driven 401/410 logout handler (no probe — decision from response body)
    this.mwApi.interceptors.response.use(
      response => response,
      this.unauthorizedResponseInterceptor,
    );

    // Request interceptors
    this.mwApi.interceptors.request.use(contextHeadersRequestInterceptor);
    this.mwApi.interceptors.request.use(this.userAgentRequestInterceptor);
    // OpenTelemetry trace propagation - inject traceparent header
    this.mwApi.interceptors.request.use(otelPropagationInterceptor);
  }

  private userAgentRequestInterceptor = async (
    axiosRequestConfig: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    // Skip setting User-Agent header on web platform to avoid CORS issues
    if (Platform.OS === 'web') {
      return axiosRequestConfig;
    }

    try {
      const userAgent = await getCachedUserAgent();
      axiosRequestConfig.headers.set('User-Agent', userAgent);
    } catch (error) {
      logWarning(error, 'apiService.setUserAgentHeader');
    }
    return axiosRequestConfig;
  };

  /**
   * Extracts x-session-id from raw AxiosResponse headers and pushes it into the
   * MobX store via callback. MUST be registered as the FIRST response interceptor,
   * before jsonApiDeserialisationResponseInterceptor strips the AxiosResponse wrapper.
   */
  private sessionIdExtractionInterceptor = (response: AxiosResponse): AxiosResponse => {
    const sessionId = response.headers?.['x-session-id'];
    if (sessionId) {
      const callbacks = getInterceptorCallbacks();
      // Fire-and-forget: MobX runInAction inside storeServerSessionId is synchronous
      void callbacks.storeServerSessionId(sessionId);
    }
    return response;
  };

  /**
   * Idempotency guard: set to true the moment logout fires so that a burst of
   * concurrent session-dead 401s only triggers one logout.
   *
   * Cleared ONLY when a new live session is established (see
   * resetUnauthorizedGuard, called from userStore on login/restore success) or
   * when the axios instance is rebuilt (initialise). It is deliberately NOT
   * cleared on arbitrary 200s: public endpoints (catalogue/search) keep
   * returning 200 while the session is dead, so clearing on success would
   * re-arm logout mid-burst and fire a second logout on the next reauth 401.
   */
  private isLoggingOut = false;

  /**
   * Clear the logout latch. Call when a live session is (re)established so a
   * fresh dead-session burst can fire logout again. Genuine session-death
   * responses carry no session id, so this is never invoked during a burst.
   */
  resetUnauthorizedGuard(): void {
    this.isLoggingOut = false;
  }

  /**
   * Arm the logout latch WITHOUT going through the 401 interceptor. Used by
   * passive (cross-tab) logout: in-flight requests in this tab are about to
   * come back 401 reauth, and they must not fire a second toast + logout.
   * Self-clears on next session establishment via resetUnauthorizedGuard.
   */
  latchUnauthorizedGuard(): void {
    this.isLoggingOut = true;
  }

  /**
   * Handles 401/410 responses.
   *
   * Decision is made entirely from the response body (DIS-1324 middleware
   * contract). No secondary probe is fired.
   *
   * Logout rule: logout iff (status === 401 || status === 410)
   *              && body?.recovery === 'reauth'
   *
   * Per-call opt-out via AxiosRequestConfig.skipUnauthorizedHandler — required
   * for endpoints that overload 401 with application semantics (e.g.
   * /verification/phone returns 401 verification_failed for a wrong OTP).
   */
  private unauthorizedResponseInterceptor = (error: unknown): Promise<never> => {
    if (!isAxiosError(error)) return Promise.reject(error);
    if (error.config?.skipUnauthorizedHandler) return Promise.reject(error);

    const {status, body} = resolveErrorBody(error);
    const isSessionDeath = (status === 401 || status === 410) && body?.recovery === 'reauth';
    if (!isSessionDeath) return Promise.reject(error);

    const callbacks = getInterceptorCallbacks();
    if (!callbacks.isUserLoggedIn?.()) return Promise.reject(error);
    if (this.isLoggingOut) return Promise.reject(error);
    this.isLoggingOut = true;

    Toast.show({
      type: 'errorMessage',
      text1: 'Session expired, please log in again.',
      visibilityTime: 4000,
    });
    callbacks.logoutUser?.();
    return Promise.reject(error);
  };

  private setHeader(headerName: string, headerValue?: string | null) {
    logger.debug('APIService', 'Setting header', {headerName, headerValue});
    if (headerValue) {
      this.mwApi.defaults.headers.common[headerName] = headerValue;
    } else {
      delete this.mwApi.defaults.headers.common[headerName];
    }
  }

  // Changes when a shop is selected
  setShopId = (shopId: string) => this.setHeader('shopId', shopId);

  // Salesforce store number (C-code) for store-specific competition entry gating (DIS-1330).
  // Distinct from shopId (MFL id): the eligibility gate keys on store_number, not the MFL id.
  setStoreNumber = (storeNumber?: string | null) => this.setHeader('x-store-number', storeNumber);

  // Changes when department is selected
  setDepartment = (departmentId?: object | null) =>
    this.setHeader('department', JSON.stringify(departmentId));

  clearDepartment = () => this.setDepartment(undefined);

  // API stuff
  pingApi = () => {
    return this.mwApi
      .get('/')
      .then(() => {
        logger.info('APIService', 'API is up', {baseURL: config.BASE_URL});
        return true;
      })
      .catch(() => {
        logger.error('APIService', 'API is down', {baseURL: config.BASE_URL});
        return false;
      });
  };

  async checkSession(): Promise<{valid: boolean; userId?: string}> {
    // Live callers: userStore.restoreSession
    // (web cookie validation on startup). NOT used by the 401 interceptor any
    // more — that decision is body-driven (DIS-1282).
    //
    // skipUnauthorizedHandler: this is an explicit liveness check that resolves
    // or rejects to its own callers; it must not re-enter the global 401
    // handler. Otherwise a dead-session probe would itself fire toast + logout
    // (double logout / logout from a path that's meant to just report status).
    const response = await this.mwApi.get<{valid: boolean; userId?: string}>('/ecommerce/session', {
      skipUnauthorizedHandler: true,
    });
    return response.data;
  }

  // Competition related
  createCompetitionEntry = async (
    body: CompetitionEntry,
  ): Promise<CreateCompetitionEntryResponse> => {
    const entryId = uuid();

    const formData = new FormData();

    formData.append('id', entryId);

    // copy text values into formdata
    (Object.keys(body) as Array<keyof CompetitionEntry>)
      .filter(key => key !== 'receiptImage')
      .filter(key => typeof body[key] !== 'undefined' && body[key] !== 'null' && body[key] !== null)
      .forEach(key => formData.append(key, body[key]));

    // copy receipt image if present
    if (body.receiptImage) {
      let image;
      let filename;
      const filenamePrefix = `competition-entry-id-${entryId}-receipt`;

      // Spec: https://github.com/react-native-image-picker/react-native-image-picker#asset-object
      if (Platform.OS === 'web') {
        // In Web, the URI is a "data" URI.
        // Example: data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAlgC
        const {uri} = body.receiptImage;
        image = dataURItoBlob(uri);
        const dataUriMediaType = getDataUriMediaType(uri);
        const filenameExtension = dataUriMediaType.split('/').pop();
        filename = `${filenamePrefix}.${filenameExtension}`;
      } else {
        // In iOS, the URI is a "file" URI.
        // Example: file:///Users/.../Library/Developer/CoreSimulator/Devices/<GUID>/data/Containers/Data/Application/<GUID>/tmp/<GUID>.jpg
        image = {
          // @ts-expect-error - FormData name property type mismatch
          name: `${filenamePrefix}-${body.receiptImage.uri.split('/').pop()}`,
          type: body.receiptImage.type,
          uri: body.receiptImage.uri,
        };
      }

      // The third argument, 'filename', is only required for Web
      formData.append('receiptImage', image, filename);
    }

    try {
      const response = await this.mwApi.post<CreateCompetitionEntryResponse>(
        '/competitions',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return response.data;
    } catch (err) {
      logError(err, 'apiService.createCompetitionEntry');
      throw err;
    }
  };

  createGameCompetitionEntry = async (): Promise<CreateCompetitionEntryResponse> => {
    try {
      const response = await this.mwApi.get<CreateCompetitionEntryResponse>(
        '/competition-entries/summer-cash-grab/entry',
      );
      return response.data;
    } catch (err) {
      logError(err, 'apiService.createGameCompetitionEntry');
      throw err;
    }
  };

  // SHOP related

  // Only used when in DEV tenant
  getShopsFromMFL = () =>
    this.mwApi
      .get<Shop[]>('/ecommerce/shops')
      // TODO: fix the JSON API deserialisation mess
      .then(res => res as unknown as Shop[])
      .catch(error => {
        logger.error('APIService', 'Error getting shop list from MFL', {error});
        return [];
      });

  findSalesforceMatch = async (sfId: string) =>
    this.mwApi.get<Shop[]>('/ecommerce/shop-matches', {
      params: {storeNumber: sfId},
    }) as unknown as Shop[]; // TODO: fix the JSON API deserialisation mess

  // findSalesforceMatch = async (sfId: string) => {
  //   try {
  //     const res = await this.mwApi.get<Shop[]>('/ecommerce/shop-matches', {
  //       params: {storeNumber: sfId},
  //     });
  //     // TODO: fix the JSON API deserialisation mess
  //     return res as unknown as Shop[];
  //   } catch (err) {
  //     console.log('error matching shop for id', sfId, err);
  //     return [];
  //   }
  // };

  getLiquorLicense = (sfId: string | null) => {
    return this.mwApi
      .get('/content/license', {params: {storeId: sfId}})
      .then(res => res.data)
      .catch(err => {
        logger.error('APIService', 'Error loading liquor license', {sfId, ...serializeError(err)});
      });
  };

  getDarksiteId = async (sfId: string) => {
    const res = await this.mwApi.get('/content/darksite', {
      params: {storeId: sfId},
    });
    logger.debug('APIService', 'Found darksite', {sfId, data: res.data});
    return res.data;
  };

  searchSuburbs = async (searchText: string, cancelToken?: CancelTokenSource) => {
    try {
      const response = await this.mwApi.get<GeocodedSuburb[]>(
        `/geolocation/suburbs/search/${searchText}`,
        {
          cancelToken: cancelToken?.token,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (!axios.isCancel(error)) {
        logger.error('APIService', 'Something went wrong cancelling the suburb lookup request', {
          error,
        });
      }
      const axiosError = error as {status?: number; body?: unknown};
      logger.debug('APIService', 'Geocode error', {
        status: axiosError.status,
        body: axiosError.body,
      });
      return [];
    }
  };

  reverseGeocode = (lat: number, lon: number) => {
    return this.mwApi
      .get('/geolocation/suburbs/reverse-geocode', {
        params: {
          lat: lat,
          lon: lon,
        },
      })
      .then(response => {
        return response.data as GeocodedSuburb[];
      })
      .catch(error => {
        logger.debug('APIService', 'Reverse geocode error', {
          status: error.status,
          body: error.body,
          lat,
          lon,
        });
        return [] as GeocodedSuburb[];
      });
  };

  addressSearch = async (query: string, {token}: CancelTokenSource) => {
    const response = await this.mwApi.get<Address[]>('/geolocation/addresses/search', {
      cancelToken: token,
      params: {query},
    });
    return response.data;
  };

  searchShopBySalesforceId = async (storeNumber: string) => {
    try {
      const res = await this.mwApi.get(`/members/member/${storeNumber}`);
      return res.data;
    } catch (error) {
      logger.error('APIService', 'Unable to lookup store', {storeNumber, error});
    }
  };

  searchShopsBySuburbPostcodeLatLon = async (
    suburb: string | null,
    postcode: string | undefined,
    lat: number | undefined,
    lon: number | undefined,
    maxDistance: number,
  ) => {
    try {
      const res = await this.mwApi.get<Shop[]>(
        `/members/search/${suburb}/${postcode}/${lat}/${lon}`,
        {
          params: {
            maxDistance,
          },
        },
      );
      return res.data;
    } catch (err) {
      logger.error('APIService', 'Error searching shops by suburb/postcode/lat/lon', {
        suburb,
        postcode,
        lat,
        lon,
        maxDistance,
        ...serializeError(err),
      });
      return [];
    }
  };

  getNearestStore = (params?: {lat: number; lon: number}) => {
    return this.mwApi
      .get<{shop: Shop; source: 'geo' | 'fallback'}>('/members/nearest', {params})
      .then(res => res.data)
      .catch(err => {
        logger.error('APIService', 'Unable to get nearest store', serializeError(err));
        throw err;
      });
  };

  searchShopsByServiceType = async (suburb: string, postcode: string, serviceType: string) => {
    try {
      const res = await this.mwApi.get<Shop[]>(
        `/ecommerce/locality-service/${suburb}/${postcode}/${serviceType}`,
      );
      // TODO: fix the JSON API deserialisation mess
      return res as unknown as Shop[];
    } catch (err) {
      logger.error('APIService', 'Error searching shops by service type', {
        suburb,
        postcode,
        serviceType,
        ...serializeError(err),
      });
      return [];
    }
  };

  getOpenDeliveryTimes = async () => {
    try {
      const res = await this.mwApi.get('/ecommerce/delivery-times');
      return res;
    } catch (err) {
      logError(err, 'apiService.getOpenDeliveryTimes');
      return [];
    }
  };

  getOpenPickupTimes = async () => {
    try {
      const res = await this.mwApi.get('/ecommerce/pickup-times');
      return res;
    } catch (err) {
      logError(err, 'apiService.getOpenPickupTimes');
      return [];
    }
  };

  //
  // FEED
  //
  getFeedList(storeNumber: string, storeState: string): Promise<AirtableFeedItem[]>;
  getFeedList(
    storeNumber: string,
    storeState: string,
    params: FeedQueryParams,
  ): Promise<PaginatedFeedResponse<AirtableFeedItem>>;
  getFeedList(
    storeNumber: string,
    storeState: string,
    params?: FeedQueryParams,
  ): Promise<AirtableFeedItem[] | PaginatedFeedResponse<AirtableFeedItem>> {
    const queryParams: Record<string, string | number> = {storeNumber, storeState};
    if (params?.offset !== undefined) queryParams.offset = params.offset;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    if (params?.segment) queryParams.segment = params.segment;

    return this.mwApi
      .get('/content/feed', {params: queryParams})
      .then(response => response.data)
      .catch(err => {
        logger.error('APIService', 'Error getting feed list', {
          storeNumber,
          storeState,
          ...(params ?? {}),
          ...serializeError(err),
        });
        // Paginated callers (useInfiniteQuery) MUST see the rejection so
        // TanStack can drive retry/error UI. Legacy flat-array callers keep
        // the back-compat empty-array swallow.
        if (params) throw err;
        return [] as AirtableFeedItem[];
      });
  }

  /**
   * DIS-1501-B / DIS-1503. Stores-by-distance, ordered/paginated by the middleware.
   * Path `:tenant` is source of truth; the shared axios instance sets a global
   * `tenant: BASE_TENANT` default header. Pin it to the path tenant per-call so
   * header === path (the endpoint 400s on a mismatch). Same header-override
   * mechanism as getDepartmentLines' `department` strip.
   */
  getStoresByTenant = (
    tenant: string,
    params: {
      lat: number;
      lon: number;
      suburb?: string;
      postcode?: string;
      state?: string;
      offset?: number;
      limit?: number;
    },
  ): Promise<PaginatedFeedResponse<StoreByDistance>> => {
    const queryParams: Record<string, string | number> = {lat: params.lat, lon: params.lon};
    if (params.suburb) queryParams.suburb = params.suburb;
    if (params.postcode) queryParams.postcode = params.postcode;
    if (params.state) queryParams.state = params.state;
    if (params.offset !== undefined) queryParams.offset = params.offset;
    if (params.limit !== undefined) queryParams.limit = params.limit;

    return this.mwApi
      .get<PaginatedFeedResponse<StoreByDistance>>(`/members/stores/${tenant}`, {
        params: queryParams,
        headers: {tenant},
      })
      .then(res => res.data);
  };

  /**
   * Get homepage content
   */
  getHomepage = (
    storeNumber: string,
    storeState: string,
    page?: string,
  ): Promise<HomepageResponse> => {
    return this.mwApi
      .get('/content/homepage', {
        params: {
          storeNumber,
          storeState,
          ...(page && {page}),
        },
      })
      .then(({data}) => data)
      .catch(err =>
        logger.error('APIService', 'Error getting homepage', {
          storeNumber,
          storeState,
          ...serializeError(err),
        }),
      );
  };

  searchByBarcode = (productIDs: string[]) => {
    return this.mwApi
      .get('/ecommerce/search-productcodes', {
        params: {
          productIDs: productIDs,
        },
      })
      .then(res => {
        // The jsonApiDeserialisationResponseInterceptor already deserializes
        // the JSON:API response — res IS the deserialized data, not an axios response.
        // If it's already an array (deserialized), return it directly.
        // If it still has .data (wasn't deserialized), unwrap it.
        if (Array.isArray(res)) return res;
        return res?.data ?? res;
      });
  };

  //
  // DISCOVER
  //
  getSegments = (storeNumber: string, storeState: string): Promise<string[]> => {
    return this.mwApi
      .get('/content/discover/segments', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => response.data as string[])
      .catch(err => {
        logger.error('APIService', 'Error getting segments', {
          storeNumber,
          storeState,
          ...serializeError(err),
        });
        return [];
      });
  };

  //
  // RANGE
  //
  getDepartments = async () => this.mwApi.get('/ecommerce/departments') as unknown as Department[];

  getDepartmentLines = async (
    params: GetDepartmentLinesRequestParams,
    options?: {unscoped?: boolean},
  ) =>
    this.mwApi.get('/ecommerce/departments/lines', {
      params,
      // When unscoped, strip the default `department` header that
      // rangeStore.selectDepartment() sets on this axios instance.
      // Without this, global search inherits the Shop's department scope
      // and returns department-filtered results instead of global results.
      ...(options?.unscoped ? {headers: {department: ''}} : {}),
    });

  getProductById = async (productId: string) => {
    const products = (await this.mwApi.get(
      `/ecommerce/products/${productId}`,
    )) as unknown as OrderLine[];
    return products[0];
  };

  getProductsByDepartmentAndBrand = async (brand: {brand: string}, department: {id: string}) => {
    const customHeader = {
      department: JSON.stringify({department_id: department.id}),
    };
    try {
      const res = await this.mwApi.get('/ecommerce/brand/department/lines', {
        headers: customHeader,
        params: {
          facets: [`brand_name:${brand.brand}`],
        },
      });
      return res;
    } catch (err) {
      logError(err, 'apiService.getProductsByDepartmentsByBrandId');
      return [];
    }
  };

  getSortOptions = async () => this.mwApi.get('/ecommerce/sort-options');

  getFilterOptions = async () => this.mwApi.get('/ecommerce/department-filters');

  //
  // Search
  //
  searchProductsKeyword = (keyword: string, cancelToken: CancelTokenSource) => {
    return this.mwApi
      .get('/ecommerce/search', {
        cancelToken: cancelToken.token,
        params: {
          keywords: keyword,
        },
      })
      .then(res => res)
      .catch(err => {
        if (!axios.isCancel(err)) {
          logError(err, 'apiService.searchProductsKeyword');
        } else throw err;
      });
  };

  searchPromotedAtCheckout = (searchId: string) => {
    return this.mwApi
      .get('/ecommerce/search', {
        params: {
          embedded_search_id: searchId,
        },
      })
      .then(res => res)
      .catch(err =>
        logger.debug('APIService', 'Error searching promoted at checkout', {
          searchId,
          ...serializeError(err),
        }),
      );
  };

  /**
   * Required to fetch lines and sticker data
   * @param params
   * @returns
   */
  searchLines = (params: {
    product_ids?: string[];
    product_codes?: string[];
    order_id?: string;
    member_price?: 'private' | 'public';
  }) => {
    return this.mwApi
      .get('/ecommerce/search', {
        params,
      })
      .then(res => res)
      .catch(err =>
        logger.debug('APIService', 'Error searching lines', {params, ...serializeError(err)}),
      );
  };

  //
  // Brands
  //
  getBrands = async () => {
    try {
      const response =
        await this.mwApi.get<Array<{id: string; name: string; imageUrl?: string}>>(
          '/content/brands',
        );
      if (!Array.isArray(response.data)) throw new Error('Brands response is not an array');
      return response.data;
    } catch (err) {
      logger.error('APIService', 'Error getting brands', serializeError(err));
      return [];
    }
  };

  //
  // Activations
  //
  // DIS-1563 split the server-side read in two. `/promotions` still concatenates both,
  // and stays here for GiftCardsPromotionDetailRootFlowScreen, which has no rejection
  // handler. The two split methods REJECT on failure on purpose: the point of the split
  // is that a winners outage is distinguishable from "there are no winners", and
  // resolving to [] would throw that distinction away again (DIS-1567).
  getPromotions = (storeNumber: string, storeState?: string): Promise<AirtablePromotionItem[]> => {
    return this.mwApi
      .get<AirtablePromotionItem[]>('/content/promotions', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => response.data)
      .catch(err => {
        // Returns [], not the result of logger.error: logger.error returns void, so the
        // chain used to resolve to `undefined` and the caller's .filter threw a TypeError
        // that masked the real network error (DIS-1567).
        logger.error('APIService', 'Error getting promotions', {
          storeNumber,
          storeState,
          ...serializeError(err),
        });
        return [];
      });
  };

  getLivePromotions = (
    storeNumber: string,
    storeState?: string,
  ): Promise<AirtablePromotionItem[]> => {
    return this.mwApi
      .get<AirtablePromotionItem[]>('/content/promotions/live', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => response.data);
  };

  getDrawnWinnersPromotions = (
    storeNumber: string,
    storeState?: string,
  ): Promise<AirtablePromotionItem[]> => {
    return this.mwApi
      .get<AirtablePromotionItem[]>('/content/promotions/winners', {
        params: {
          storeNumber,
          storeState,
        },
      })
      .then(response => response.data);
  };

  applyOffer = async (mflId: string, zenGlobalId?: string): Promise<ApplyOfferSuccessResponse> => {
    const res = await this.mwApi.post('/ecommerce/v3/order-apply-offer', {
      mflId,
      ...(zenGlobalId && {zenGlobalId}),
    });
    return res.data;
  };

  /**
   * Clears all activated loyalty deals from the current order.
   * Used for recovery when offers expire during checkout.
   *
   * DELETE /ecommerce/order-deals
   *
   * BACKEND DEPENDENCY: This endpoint must exist in middleware.
   * Ticket: DIS-643
   * Expected response: { success: boolean, activated_deals: string[] }
   *
   * If endpoint doesn't exist, clearDealsAndRetryCheckout will fail
   * and show "Unable to clear expired offer" toast to user.
   */
  clearOrderDeals = async (): Promise<{success: boolean; activated_deals: string[]}> => {
    const res = await this.mwApi.delete('/ecommerce/order-deals');
    return res.data;
  };

  /**
   * Fetch offers from v2 endpoint with pre-merged data
   * @param storeNumber - Zen store number (e.g., TEST-SNS-LOYALTY) for coffee card stats
   * @param zenCardNumber - Optional Zen card for coffee card progress
   */
  getOffersV2 = async (
    storeNumber?: string,
    zenCardNumber?: string,
  ): Promise<OffersItemWithMflId[]> => {
    if (!storeNumber) {
      logger.warn('ApiService', 'getOffersV2 called without storeNumber, returning empty array');
      return [];
    }

    try {
      const response = await this.mwApi.get<V2OffersResponse>('/ecommerce/v2/offers', {
        params: {storeNumber},
        headers: {
          ...(zenCardNumber ? {'x-zen-global-id': zenCardNumber} : {}),
        },
      });

      return response.data.offers.map(transformV2OfferToLegacy);
    } catch (err) {
      logger.error('ApiService', 'Error fetching v2 offers', serializeError(err));
      throw err;
    }
  };

  getSignUpOffers = async () => {
    const response = await this.mwApi.get<SignUpOfferData[]>(`/content/signupOffers`);
    return response.data;
  };

  getZenOffer = async (offerId: string) => {
    const response = await this.mwApi.get<ZenOffer>(`/rewards/offers/${offerId}`);
    return response.data;
  };

  // User Account
  validateEmail = async (email: string) =>
    this.mwApi.post<{valid: boolean; message: string}>('/validate-email', {data: {email}});

  checkEmail2 = async (email: string) =>
    this.mwApi.get('/ecommerce/email-check-v2', {
      params: {
        email: email,
      },
    });

  convertLiteUser = async (body: {
    email: string;
    password: string;
    billing_address: BillingAddress;
    bypass_compromised_password_validation: boolean;
  }) => this.mwApi.post('/ecommerce/convert-lite-user', body);

  verifyEmail = async (email: string, password: string, verificationCode: string) => {
    return this.mwApi
      .post('/ecommerce/verify-email', {
        email,
        password,
        verification_code: verificationCode,
      })
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to verify email', {email, ...serializeError(err)});
        return false;
      });
  };

  login2 = async (body: {email: string; password: string}) => {
    return this.mwApi
      .post('/ecommerce/login-v2', body)
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Login v2 error', {email: body.email, ...serializeError(err)});
        throw err;
      });
  };

  logout = async () => {
    try {
      const res = await this.mwApi.post('/ecommerce/logout');
      return res;
    } catch (err) {
      logger.error('APIService', 'Logout error', serializeError(err));
    }
  };

  transferSession = async (): Promise<TransferTokens | null> => {
    try {
      // the interceptors mess up the response type
      const response: unknown = await this.mwApi.post<TransferTokens>(
        '/ecommerce/session-transfer',
      );
      return response as TransferTokens;
    } catch (error: unknown) {
      logError(error, 'apiService.transferSession');
      return null;
    }
  };

  getUserInfo = (): Promise<AxiosResponse<User> | null> => {
    return this.mwApi
      .get<User>('/ecommerce/user')
      .then(res => res)
      .catch(error => {
        logger.error('APIService', 'Unable to retrieve user information', {error});
        return null;
      });
  };

  getUserInfoV2 = async (options?: {retryable?: boolean; zohoId?: string}): Promise<UserV2> =>
    this.mwApi
      .get<UserInfoV2Response>('/ecommerce/v2/user', {
        params: {
          ...(options?.retryable ? {retryable: true} : {}),
          ...(options?.zohoId ? {zohoId: options.zohoId} : {}),
        },
      })
      .then(res => {
        return transformUserV2(res.data);
      })
      .catch(err => {
        logError(err, 'apiService.getUserInfoV2');
        throw err;
      });

  updateUserInfo = async (
    actionKey: keyof typeof AccountScreenAction,
    user: User,
    options?: {
      skipConvert?: boolean | undefined;
      verificationCode?: string;
    },
  ) => {
    const {skipConvert, verificationCode} = options || {};
    const mflUser = skipConvert ? user : convertAppUsertoMFLUser(user);
    const payload = {
      ...mflUser,
      action: AccountScreenAction[actionKey],
      verification_code: verificationCode,
    };
    return await this.mwApi.patch('/ecommerce/user', payload);
  };

  checkPasswordStrength = async (user: User) =>
    this.mwApi.post('/ecommerce/validate-user', convertAppUsertoMFLUser(user));

  sendMobileVerificationCode = async (data: MobileVerificationParams) => {
    try {
      if (data.phoneNumber?.indexOf('0') === 0) {
        data.phoneNumber = '+61' + data.phoneNumber.substring(1);
      }

      // re-using rewards endpoint, could change this to generic mwApi endpoint
      // later for semantic reasons
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: false,
        },
      });
    } catch (err) {
      logError(err, 'apiService.sendMobileVerificationCode');
      return null;
    }
  };

  // TODO this will be entirely removed (changed to createUserNew) after
  // all the brands have deployed the rewards/loyalty piece
  createUser = async (user: User) =>
    await this.mwApi.post('/ecommerce/user', convertAppUsertoMFLUser(user));

  createUserNew = async (
    user: User,
    selectedStore: {
      id: string;
      name: string;
    },
    rewardsCheckbox?: boolean | undefined,
    verificationCode?: number | undefined,
    signUpCode?: string | '',
  ) => {
    return this.mwApi.post('/aggregate/create-account', {
      ...convertAppUsertoMFLUser(user),
      selectedStore,
      rewardsCheckbox,
      verificationCode,
      ...(signUpCode !== undefined ? {signUpCode} : {}),
    });
  };

  deleteUser = async (email: string, phoneNumber: string, lastName: string) => {
    try {
      return await this.mwApi.delete(
        `/ecommerce/user?email=${email}&phone=${phoneNumber}&lastName=${lastName}`,
      );
    } catch (err: unknown) {
      let message = 'Unable to delete user';

      // Runtime validation: Check if error is an AxiosError with expected response structure
      if (isAxiosError<MflErrorResponse>(err)) {
        // Axios 1.x fetch adapter stores response in error.cause.response when MSW intercepts
        // Additionally, the data might be a string that needs parsing
        type CauseWithResponse = {response?: {data?: MflErrorResponse | string}};
        const causeData = (err.cause as CauseWithResponse | undefined)?.response?.data;
        const rawData = err.response?.data ?? causeData;

        // Parse if string (axios 1.x fetch adapter sometimes returns unparsed JSON)
        let responseData: MflErrorResponse | undefined;
        if (typeof rawData === 'string') {
          try {
            responseData = JSON.parse(rawData);
          } catch {
            // Not valid JSON, ignore
          }
        } else {
          responseData = rawData as MflErrorResponse;
        }

        const errorDetails = responseData?.errors?.errors?.map(e => e.detail).join(', ');

        if (errorDetails) {
          // Add a period if needed
          const formattedDetails =
            errorDetails.slice(-1) !== '.' ? `${errorDetails}.` : errorDetails;
          message = `${message}: ${formattedDetails} Please contact the helpdesk for support.`;
        }
      }

      throw new Error(message);
    }
  };

  initiatePasswordReset = (values: {email_address: string}) => {
    return this.mwApi
      .post('/ecommerce/user/password', {...values})
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to initiate password reset', {
          email: values.email_address,
          ...serializeError(err),
        });
        return err;
      });
  };

  establishSessionWithToken = (token: string) => {
    return this.mwApi
      .post('/ecommerce/session/token', {
        token: token,
      })
      .then(res => res)
      .catch(err => {
        logger.error(
          'APIService',
          'Unable to establish session with reset token',
          serializeError(err),
        );
        throw err;
      });
  };

  getWishlists = () => {
    return this.mwApi
      .get('/ecommerce/wishlist')
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to retrieve wishlists for user', serializeError(err));
        throw err;
      });
  };

  createWishlist = (name: string) => {
    return this.mwApi
      .post('/ecommerce/wishlist', {shopping_list: {name}})
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to create new wishlist', {name, ...serializeError(err)});
        throw err;
      });
  };

  addItemToWishlist = (wishlistId: string, productId: string, barcode: string) => {
    return this.mwApi
      .post(`/ecommerce/wishlist/${wishlistId}/contents/`, {
        productId: productId,
        barcode: barcode,
      })
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to add item to wishlist', {
          wishlistId,
          productId,
          barcode,
          ...serializeError(err),
        });
        throw err;
      });
  };

  getItemsInWishlist = (id: string) => {
    return this.mwApi
      .get(`/ecommerce/wishlist/${id}/contents`)
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to retrieve wishlist items', {
          id,
          ...serializeError(err),
        });
        throw err;
      });
  };

  removeItemFromWishlist = (wishlistId: string, entryId: string, barcode: string) => {
    return this.mwApi
      .delete(`/ecommerce/wishlist/${wishlistId}/contents/${entryId}`, {
        data: {barcode: barcode},
      })
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to remove item from wishlist', {
          wishlistId,
          entryId,
          barcode,
          ...serializeError(err),
        });
        throw err;
      });
  };

  // Orders
  loadCurrentOrder = () => {
    return this.mwApi
      .get('/ecommerce/order')
      .then(res => res.data[0])
      .catch(err =>
        logger.error('APIService', 'Unable to load current order', serializeError(err)),
      );
  };

  loadRecentOrders = async () => {
    const res = await this.mwApi.get('/ecommerce/order/recent');
    return res.data;
  };

  changeOrderLines = (lines: {product_id: string; quantity: number}[]) => {
    return this.mwApi
      .put('/ecommerce/order', {
        lines: [...lines],
      })
      .then(res => res.data)
      .catch(err => {
        logger.error('APIService', 'Unable to update order lines', {
          lineCount: lines.length,
          ...serializeError(err),
        });
        throw new Error('Unable to update order');
      });
  };

  updateCoupon = async (coupon: string | null) => {
    const res = await this.mwApi.patch('/ecommerce/coupon', {coupon});
    return res.data;
  };

  validateOrder = async () => {
    const res = await this.mwApi.post('/ecommerce/v2/order-validate');
    return res.data;
  };

  /**
   * Complete order checkout with 3DS support
   * POST /ecommerce/v2/order-checkout
   *
   * Supports two payment methods:
   * - Saved card: payment_instrument_id
   * - Apple Pay: single_use_payment_token
   */
  checkoutOrder = async (data: CheckoutOrderData) => {
    const res = await this.mwApi.post('/ecommerce/v2/order-checkout', {
      order: data,
    });
    return res.data;
  };

  pollOrder = (orderId: string) => {
    return this.mwApi
      .get(`/ecommerce/orders/${orderId}`)
      .then(res => res.data)
      .catch(err => {
        logger.error('APIService', 'Unable to poll order status', {
          orderId,
          ...serializeError(err),
        });
        throw err;
      });
  };

  getPickupTimeSlots = () => {
    return this.mwApi
      .get('/ecommerce/pickup-slots')
      .then(res => res)
      .catch(err =>
        logger.error('APIService', 'Unable to load pickup slots for order', serializeError(err)),
      );
  };

  getDeliveryTimeSlots = (regionId: string) => {
    return this.mwApi
      .get('/ecommerce/delivery-slots', {
        params: {regionId},
      })
      .then(res => res)
      .catch(err =>
        logger.error('APIService', 'Unable to load delivery slots for order', {
          regionId,
          ...serializeError(err),
        }),
      );
  };

  getRegions = (query: string) => {
    return this.mwApi
      .get('/ecommerce/regions', {params: {query}})
      .then(res => res)
      .catch(error =>
        logger.error('APIService', 'Unable to get region for address', {
          query,
          ...serializeError(error),
        }),
      );
  };

  updateDeliveryDetails = (data: {
    address?: Address;
    time_slot_id?: string;
    service_type?: string;
  }) => {
    return this.mwApi
      .patch('/ecommerce/delivery-details', data)
      .then(res => res.data)
      .catch(err =>
        logger.error('APIService', 'Unable to update delivery details', serializeError(err)),
      );
  };

  updateOrderNote = (data: {note: string}) => {
    return this.mwApi
      .patch('/ecommerce/order-note', data)
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to update order note', {
          note: data.note,
          ...serializeError(err),
        });
      });
  };

  updateGiftCardRecipientInformation = async (
    giftCardRecipientInformation: GiftCardRecipientData,
  ) => this.mwApi.patch('/ecommerce/orders/current/gift_information', giftCardRecipientInformation);

  getCompetitionsForOrder = (orderId: string) => {
    return this.mwApi
      .get(`/ecommerce/competitions/${orderId}`)
      .then(res => res)
      .catch(err => {
        logger.error('APIService', 'Unable to load eligible competitions for order', {
          orderId,
          errorCodes: err.response?.data?.errors?.map((e: {code: string}) => e.code).join(';'),
          ...serializeError(err),
        });
        return [];
      });
  };

  // Support
  createSupportTicket = async (ticket: {subject: string; description: string; email: string}) =>
    this.mwApi.post('/support/support-ticket', ticket);

  createSelfExclusionRequest = async (request: {
    email: string;
    firstName: string;
    lastName: string;
  }) => this.mwApi.post('/support/self-exclusion-request', request);

  createThirdPartyReviewRequest = async (request: {email: string; description: string}) =>
    this.mwApi.post('/support/third-party-review-request', request);

  // Nexus Catalogues (v2 - simplified endpoint)
  getCatalogueHtmlV2 = async (storeId: string): Promise<string> => {
    const response = await this.mwApi.get(`/stores/${storeId}/catalogue.html`);
    return response.data; // Raw HTML string
  };

  // 3DS
  getBrowserDataJs = async () => this.mwApi.get('/3ds/browserDataJs');

  // Gift Cards
  getGiftCardVoucherData = async (serialNumber: string, pin: string) =>
    this.mwApi.post<EnquireVoucherResponse | EnquireVoucherErrorResponse>(
      '/gift-cards/voucher/gift/enquire',
      {
        serialNumber,
        pin,
      },
    );

  // Information
  getInformation = async (contentType: string) =>
    this.mwApi.get('/content/info', {
      params: {
        contentType,
      },
    });

  getRewardsInformation = async (content: string) =>
    this.mwApi.get<RewardsInformationResponse>('/content/rewards', {
      params: {content},
    });

  createZenGiftCardPaymentInstrument = async (
    zenGiftCardsPaymentInstrument: CreateZenGiftCardPaymentInstrumentRequest,
  ) => {
    try {
      const res = await this.mwApi.post(
        '/ecommerce/gift-cards/payment-instruments',
        zenGiftCardsPaymentInstrument,
      );
      return res;
    } catch (err: unknown) {
      const axiosError = err as {response?: {status?: number; data?: unknown}};
      logger.error('APIService', 'Unable to create gift card payment instrument', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        ...serializeError(err),
      });
      throw new Error('We are having trouble verifying your Gift Card, please try again.');
    }
  };

  updateCurrentOrderTenderOptions = async (request: {
    order?: {
      tender_options?: TenderOption[];
    };
  }) => {
    try {
      const res = await this.mwApi.post('/ecommerce/v2/order-tender-options', request);
      return res;
    } catch (err: unknown) {
      const axiosError = err as {response?: {status?: number; data?: string}};
      logger.error('APIService', 'Unable to update order tender options', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        ...serializeError(err),
      });
      throw new Error(axiosError.response?.data || 'Unable to store card');
    }
  };

  rewardsOptIn = async (data: RewardsOptInParams) => {
    try {
      return await this.mwApi.post('/rewards/opt-in', data);
    } catch (err) {
      logError(err, 'apiService.rewardsOptIn');
      return null;
    }
  };

  rewardsOptOut = async (data: RewardsOptOutParams) => {
    try {
      return await this.mwApi.post('/rewards/opt-out', data);
    } catch (err) {
      logError(err, 'apiService.rewardsOptOut');
      return null;
    }
  };

  competitionOptOut = async (
    data: CompetitionOptOutParams,
  ): Promise<CompetitionOptOutResponse | null> => {
    logger.debug('APIService', 'Competition opt-out request', {data});
    try {
      const response = await this.mwApi.post<CompetitionOptOutResponse>(
        '/competition-opt-out',
        data,
      );
      return response.data;
    } catch (err) {
      logError(err, 'apiService.competitionOptOut');
      throw err; // Re-throw to allow proper error handling in the component
    }
  };

  rewardsCheckAccountExists = async (data: RewardsCheckAccountExistsParams) => {
    try {
      return await this.mwApi.post('/rewards/check-account-exists', {data});
    } catch (err) {
      logError(err, 'apiService.rewardsCheckAccountExists');
      return null;
    }
  };

  rewardsCheckMobileExists = async (data: RewardsCheckAccountExistsParams) => {
    try {
      return await this.mwApi.post('/rewards/check-mobile-exists', {data});
    } catch (err) {
      logError(err, 'apiService.rewardsCheckMobileExists');
      return null;
    }
  };

  rewardsVerifyNewUserPhoneNumber = async (data: RewardsVerifyNewPhoneNumberParams) => {
    try {
      return await this.mwApi.post('/rewards/send-verification-new-lite-user', {
        data,
      });
    } catch (err) {
      logError(err, 'apiService.rewardsVerifyNewUserPhoneNumber');
      return null;
    }
  };

  rewardsVerifyExistingUserPhoneNumber = async (data: RewardsVerifyExistingPhoneNumberParams) => {
    try {
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: false,
        },
      });
    } catch (err) {
      logError(err, 'apiService.rewardsVerifyExistingUserPhoneNumber');
      return null;
    }
  };

  rewardsVerifyExistingUserEmail = async (data: VerifyExistingUserEmailAddressParams) => {
    try {
      return await this.mwApi.post('/rewards/send-verification', {
        data: {
          ...data,
          isEmail: true,
        },
      });
    } catch (err) {
      logError(err, 'apiService.rewardsVerifyExistingUserEmail');
      return null;
    }
  };

  rewardsSignUp = async (data: RewardsSignUpParams): Promise<RewardsSignUpResult> => {
    try {
      const response = await this.mwApi.post<RewardsSignUpResponse>('/rewards/verification-lite', {
        data,
      });
      return {ok: true, data: response.data};
    } catch (err) {
      logError(err, 'apiService.rewardsSignUp');
      return mapRewardsSignUpError(err);
    }
  };

  // DIS-1190 phase 1: staff verify phone. Backend parks Temporal workflow
  // and returns a sessionToken that phase 3 must echo back. 5min TTL.
  //
  // skipUnauthorizedHandler: this endpoint overloads 401/422 with
  // verification_failed semantics (wrong/expired/max-attempts OTP). The
  // caller maps those to friendly UI messages via mapVerifyPhoneStaffError;
  // without opting out, the global interceptor would toast "Session expired"
  // and log the user out the moment they fat-finger a digit.
  verifyPhoneStaff = async (data: VerifyPhoneStaffParams): Promise<VerifyPhoneStaffResult> => {
    try {
      const response = await this.mwApi.post<{verified: boolean; sessionToken: string}>(
        '/verification/phone',
        {data},
        {timeout: 10_000, skipUnauthorizedHandler: true},
      );
      const sessionToken = response.data?.sessionToken;
      if (!sessionToken) {
        logError(
          new Error('verifyPhoneStaff success response missing sessionToken'),
          'apiService.verifyPhoneStaff',
        );
        return {ok: false, reason: 'unknown'};
      }
      return {ok: true, sessionToken};
    } catch (err) {
      logError(err, 'apiService.verifyPhoneStaff');
      return {ok: false, reason: mapVerifyPhoneStaffError(err)};
    }
  };

  // DIS-1190 phase 3: staff complete signup. Phone-keyed lookup of parked
  // workflow on backend. signUpCode is required (backend validates it now).
  // Success matches legacy /rewards/verification-lite response shape.
  rewardsSignUpComplete = async (
    data: RewardsSignUpCompleteParams,
  ): Promise<RewardsSignUpCompleteResult> => {
    try {
      const response = await this.mwApi.post<RewardsSignUpResponse>(
        '/rewards/verification-lite/complete',
        {data},
      );
      return {ok: true, data: response.data};
    } catch (err) {
      logError(err, 'apiService.rewardsSignUpComplete');
      return {ok: false, reason: mapRewardsSignUpCompleteError(err)};
    }
  };

  rewardsGetAccount = async (data: RewardsGetAccountParams) => {
    try {
      return await this.mwApi.get<RewardsGetAccountResponse>('/rewards/account-details', {
        params: {phoneNumber: data.phoneNumber},
      });
    } catch (err) {
      logError(err, 'apiService.rewardsGetAccount');
      return null;
    }
  };

  // DIS-1384: advisory invite preflight. Thin passthrough (no swallow) so the
  // caller can distinguish an explicit 403/valid:false (block, R3/R19 copy) from
  // any transient error (fail-open, proceed — submit-time 403 is the real gate).
  rewardsLmgStaffInvite = async ({code}: RewardsLmgStaffInviteParams) =>
    this.mwApi.get<RewardsLmgStaffInviteResponse>('/rewards/lmg-staff/invite', {
      params: {code},
    });

  // DIS-1384: fetch department/state option lists. Thin passthrough so the store
  // can surface a retry-capable error on failure (R25) instead of empty lists.
  rewardsGetLmgStaffOptions = async () =>
    this.mwApi.get<RewardsGetLmgStaffOptionsResponse>('/rewards/lmg-staff');

  getCommunicationsPreferences = async () => {
    try {
      return await this.mwApi.get('/aggregate/communications-preferences');
    } catch (err) {
      logError(err, 'apiService.getCommunicationsPreferences');
      return null;
    }
  };

  updateCommunicationsPreferences = async (data: CommunicationsPreferencesParams) => {
    try {
      return await this.mwApi.post('/aggregate/communications-preferences', {
        data,
      });
    } catch (err) {
      logError(err, 'apiService.updateCommunicationsPreferences');
      // DIS-1586: only 422 contact_not_found stays swallowed — there is no Zoho
      // contact to write to and no retry the member could make, so the screen
      // keeps its existing no-error behaviour for that case. Every other
      // failure is rethrown so the caller can roll the switch back instead of
      // showing a consent change the server never stored.
      if (isCommunicationsContactNotFound(err)) {
        return null;
      }
      throw err;
    }
  };

  // Tmp: Summer Cash Grab
  getSummerCashGrabHtml = async (iFrameLinkParams: string) => {
    const response = this.mwApi.get(`/games/summerCashGrab/?${iFrameLinkParams}`);
    return (await response).data as unknown as Promise<{
      initScript: string;
      gameUri: string;
      htmlContent: string;
    }>;
  };

  /**
   * Get client configuration from middleware
   * Unauthenticated endpoint - used for app version enforcement
   */
  getClientConfig = async (): Promise<ClientConfigResponse | null> => {
    try {
      const response = await this.mwApi.get<ClientConfigResponse>('/config');
      return response.data;
    } catch (err) {
      logger.error('APIService', 'Unable to fetch client config', serializeError(err));
      return null; // Fail open - don't block app on config fetch failure
    }
  };
}

const apiService = new ApiService();
export default apiService;

export enum GetDepartmentLinesSortOptionId {
  TOP_PRODUCTS = 'top_products',
  NAME = 'name',
  NAME_DESCENDING = 'name_descending',
  PRICE = 'price',
  PRICE_DESCENDING = 'price_descending',
  UNIT_PRICE = 'unit_price',
  DISCOUNT_PERCENT = 'discount_percent',
  DISCOUNT_CENTS = 'discount_cents',
}

export type GetDepartmentLinesRequestParams = {
  department_id?: string;
  sort_option_id?: GetDepartmentLinesSortOptionId;
  facets?: string[];
  page_size?: number;
  keywords?: string;
  page?: number;
  specials?: boolean;
  sell_price_cents_minimum?: number;
  sell_price_cents_maximum?: number;
  member_price?: 'private' | 'public';
};

// Exported for testing
export {transformV2CoffeeCard, transformV2ImageToAirtable, transformV2OfferToLegacy};
